import { useCallback, useMemo, useRef, useState } from 'react';

export interface PendingEdit {
    /** The grid row the cell belongs to — a billing config, an on-top cost, a mapping. */
    rowId: number;
    /** Which part of the row: `field` for a column, otherwise the monthly series' payload field name. */
    kind: string;
    /** A column name for `field`, a month key (YYYY-MM-DD) for a monthly kind. */
    key: string;
    value: unknown;
}

export interface PendingCellEdits {
    count: number;
    isDirty: boolean;
    /** Whether ANY scope holds edits, not just the current one — what the navigation guard needs, so
     *  leaving the page still warns about edits made under a version the user has since switched away
     *  from (they are retained, not discarded; see the scope note below). */
    hasEdits: boolean;
    /** The pending value for one cell, or `undefined` when the user has not touched it. `null` is a
     *  cleared cell and therefore a real answer — callers must check for `undefined`, not falsiness. */
    get: (rowId: number, kind: string, key: string) => { value: unknown } | undefined;
    /** Record an edit. `pristine` is the value the server last served for that cell, so an edit that
     *  restores the original drops out of the pending set instead of counting as a change. */
    set: (rowId: number, kind: string, key: string, value: unknown, pristine: unknown) => void;
    /** Forget a row's edits — used when the row is deleted, so the batch cannot try to write it.
     *  `kinds` limits it to one resource's kinds: the Overview's store is shared by three resources
     *  whose ids are independent sequences, so an unscoped drop would take an unrelated row's edits
     *  with it (billing config 7 and on-top cost 7 are different rows). */
    dropRow: (rowId: number, kinds?: string[]) => void;
    /** Forget NAMED cells of one row — for a write that covers only SOME of a row's columns, where the
     *  blanket `dropRow` would throw away pending edits it never wrote. Pass the keys the request
     *  actually carried (CALL-778: the Billing Config row dialog owns most columns but not the
     *  grid-only `budgetBucketId` and `isHidden`). */
    dropCells: (rowId: number, kind: string, keys: string[]) => void;
    /** Forget every edit of these kinds — for a write that replaces a whole resource out from under the
     *  store, where the rows the edits belong to no longer exist (the on-top import). */
    dropKinds: (kinds: string[]) => void;
    /** A request is going out for these edits. Until it settles they are what the SERVER is being told,
     *  so `set` measures against them instead of the served value. */
    submit: (edits: PendingEdit[]) => void;
    /** That request came back. `written` names the rows the server says it wrote — `'all'` for endpoints
     *  that do not report ids, an empty set for a failure. A written row's edit is dropped only if it is
     *  still the one that was submitted (identity), so a cell edited again mid-flight stays pending. */
    settle: (edits: PendingEdit[], written: Set<number> | 'all') => void;
    discard: () => void;
    edits: PendingEdit[];
}

const editId = (rowId: number, kind: string, key: string) => `${rowId}:${kind}:${key}`;

/** A cell with nothing in it. The three spellings all reach here: `null` from a valueParser clearing a
 *  cell, `undefined` from a month the server never stored, `''` from an emptied text column. Clearing a
 *  cell that was already empty is not an edit, so they have to compare equal. */
const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === '';

/** Numbers arrive as numbers from a valueParser but as strings from the server's DECIMAL columns
 *  (`price` is DECIMAL(10,4)), so compare numerically when ONE side is already a number.
 *
 *  Only then: two STRINGS that merely read as the same number are not the same value. Text columns go
 *  through here too, and coercing them made retyping a Kostenstelle from "0815" to "815" (or a PR name
 *  from "1.0" to "1") look like a restore — the edit was dropped, no marker appeared, Save stayed
 *  disabled, and the next refetch put the old string back. */
const isSameValue = (a: unknown, b: unknown): boolean => {
    if (isEmptyValue(a) || isEmptyValue(b)) return isEmptyValue(a) && isEmptyValue(b);
    if (a === b) return true;
    if (typeof a !== 'number' && typeof b !== 'number') return false;
    const left = Number(a);
    const right = Number(b);
    return !Number.isNaN(left) && !Number.isNaN(right) && left === right;
};

/** The store of a scope nobody has edited yet. Module-level so `edits`/`get` keep stable identities. */
const NO_EDITS: ReadonlyMap<string, PendingEdit> = new Map();

// The pending-edit store behind a page's one Save (CALL-779). The Budget grids used to persist inside
// onCellValueChanged — a request per cell, each replacing a row's whole month series — so a page of edits
// was a burst of overlapping writes and any of them could lose. Edits now land here and the page posts
// them together.
//
// Keyed by (row, kind, cell), which gives the two things every caller needs for free: a cell edited
// twice keeps only its final value (the "rapid edits across many cells" case, with no dedup code), and a
// grid can ask about one cell in constant time while painting it.
//
// Deliberately knows NOTHING about request shapes. One page's Save is one endpoint (Billing Config,
// Prices & ABT) but the Overview's is three — baselines, forecast volumes, on-top costs — so turning
// `edits` into requests belongs to the page, not here. See usePendingConfigEdits for the billing-config
// builder and overviewEditPayloads for the Overview's.
//
// `scope` is what the row ids are relative to — the Overview passes its budget version. Edits are kept
// PER SCOPE and only the current scope's are read, because switching version is a `?versionId=` change
// on the same route: the page does not remount and the unsaved-changes guard deliberately ignores it, so
// a single flat store meant version A's pending figures were painted onto version B's rows and written
// into B by Save. Retained rather than discarded on a switch, so going back to A finds the edits still
// there; `hasEdits` is what keeps them from being lost silently when the page finally unmounts.
//
// The billing-config pages pass NO scope on purpose: their rows are global master data keyed by config
// id, so an edit made under one bucket filter stays saveable under another (see BudgetBillingConfig).
export const usePendingCellEdits = (scope: string | number = ''): PendingCellEdits => {
    const scopeKey = String(scope);
    const [byScope, setByScope] = useState<Map<string, Map<string, PendingEdit>>>(() => new Map());
    const pending = byScope.get(scopeKey) ?? NO_EDITS;
    /** editId → the value a request currently out is writing. A ref: nothing renders from it. */
    const inFlight = useRef<Map<string, unknown>>(new Map());

    // Every mutation rewrites just this scope's map, and an emptied scope is dropped so `hasEdits` is
    // simply "is anything left anywhere".
    const update = useCallback(
        (change: (current: ReadonlyMap<string, PendingEdit>) => Map<string, PendingEdit>) => {
            setByScope((previous) => {
                const changed = change(previous.get(scopeKey) ?? NO_EDITS);
                const next = new Map(previous);
                if (changed.size) next.set(scopeKey, changed);
                else next.delete(scopeKey);
                return next;
            });
        },
        [scopeKey]
    );

    const set = useCallback(
        (rowId: number, kind: string, key: string, value: unknown, pristine: unknown) => {
            update((current) => {
                const next = new Map(current);
                const id = editId(rowId, kind, key);

                // What "no change" means is whatever the SERVER is about to hold: the served value
                // normally, but the submitted one while a save for this cell is in flight. Measuring a
                // revert against the served value there lost it — served 1, type 2, Save, type 1 again
                // and the entry was deleted as "back to pristine" while the request went on to commit 2.
                const baseline = inFlight.current.has(id) ? inFlight.current.get(id) : pristine;

                if (isSameValue(value, baseline)) {
                    next.delete(id);
                } else {
                    next.set(id, { rowId, kind, key, value });
                }

                return next;
            });
        },
        [update]
    );

    const dropRow = useCallback(
        (rowId: number, kinds?: string[]) => {
            update((current) => {
                const next = new Map(current);
                for (const [id, edit] of current) {
                    if (edit.rowId === rowId && (!kinds || kinds.includes(edit.kind))) next.delete(id);
                }
                return next;
            });
        },
        [update]
    );

    const dropCells = useCallback(
        (rowId: number, kind: string, keys: string[]) => {
            update((current) => {
                const next = new Map(current);
                for (const key of keys) next.delete(editId(rowId, kind, key));
                return next;
            });
        },
        [update]
    );

    const dropKinds = useCallback(
        (kinds: string[]) => {
            update((current) => {
                const next = new Map(current);
                for (const [id, edit] of current) {
                    if (kinds.includes(edit.kind)) next.delete(id);
                }
                return next;
            });
        },
        [update]
    );

    const submit = useCallback((submitted: PendingEdit[]) => {
        for (const edit of submitted) {
            inFlight.current.set(editId(edit.rowId, edit.kind, edit.key), edit.value);
        }
    }, []);

    const settle = useCallback(
        (submitted: PendingEdit[], written: Set<number> | 'all') => {
            // Cleared outside the updater: React may call an updater twice, and this is a ref.
            for (const edit of submitted) inFlight.current.delete(editId(edit.rowId, edit.kind, edit.key));

            update((current) => {
                const next = new Map(current);
                for (const edit of submitted) {
                    // A row the server did not report as written (a bulk endpoint skips ones that have
                    // been deleted) keeps its edits: clearing them would lose the user's work under a
                    // success toast.
                    if (written !== 'all' && !written.has(edit.rowId)) continue;

                    const id = editId(edit.rowId, edit.kind, edit.key);
                    // Identity, not the key: `set` stores a fresh object, so a cell re-edited while the
                    // request was in flight holds a different one and must survive the clear.
                    if (next.get(id) === edit) next.delete(id);
                }
                return next;
            });
        },
        [update]
    );

    const discard = useCallback(() => update(() => new Map()), [update]);

    const get = useCallback(
        (rowId: number, kind: string, key: string) => pending.get(editId(rowId, kind, key)),
        [pending]
    );

    const edits = useMemo(() => [...pending.values()], [pending]);

    return {
        count: pending.size,
        isDirty: pending.size > 0,
        hasEdits: byScope.size > 0,
        get,
        set,
        dropRow,
        dropCells,
        dropKinds,
        submit,
        settle,
        discard,
        edits,
    };
};
